import {
  MIME_TO_EXTENSION,
  VECTOR_STORE_SUPPORTED_EXTENSIONS,
} from "../constants/fileConstants";

// Function to check if file type is supported by vector store
export const isFileTypeSupported = (fileName) => {
  if (!fileName) return false;
  const extension = `.${fileName.split(".").pop().toLowerCase()}`;
  return VECTOR_STORE_SUPPORTED_EXTENSIONS.includes(extension);
};

// Enhanced function to check if file is supported by vector store (handles both filename and MIME type)
export const isFileTypeSupportedEnhanced = (fileName, mimeType) => {
  // First try the filename approach
  if (fileName && isFileTypeSupported(fileName)) {
    return true;
  }

  // If filename check fails, try MIME type mapping
  if (mimeType) {
    const extension = MIME_TO_EXTENSION[mimeType.toLowerCase()];
    if (extension && VECTOR_STORE_SUPPORTED_EXTENSIONS.includes(extension)) {
      return true;
    }
  }

  return false;
};

// Keep the detectNeedsAnalysis function but enhance it to better identify files for embedding analysis
export const detectNeedsAnalysis = (file) => {
  // First check if file has the explicit flag set from previous processing
  if (file.needsAnalysis === true || file.needsEmbeddings === true) {
    return true;
  }

  // Check if file is already attached to a vector store
  if (file.vector_store_attached || file.vector_store_file_id) {
    return true;
  }

  // Check if file type is supported by vector store
  if (isFileTypeSupported(file.name || "")) {
    return true;
  }

  // Get file extension
  const fileName = file.name || "";
  const extension = fileName.split(".").pop().toLowerCase();

  // Check file MIME type
  const mimeType = file.type || "";

  // Define types that need analysis
  const analysisExtensions = [
    "txt",
    "md",
    "markdown",
    "csv",
    "json",
    "xml",
    "js",
    "jsx",
    "ts",
    "tsx",
    "py",
    "java",
    "c",
    "cpp",
    "h",
    "rb",
    "go",
    "rs",
    "php",
    "html",
    "css",
    "sql",
    "yaml",
    "yml",
  ];

  const analysisMimeTypes = [
    "text/",
    "application/json",
    "application/xml",
    "application/javascript",
    "application/typescript",
    "application/x-python",
    "application/x-ruby",
    "application/x-php",
    "application/x-yaml",
  ];

  // Check if extension or MIME type indicates the file needs analysis
  return (
    // Not an image (we handle those separately)
    !mimeType.startsWith("image/") &&
    // Not a PDF (we handle those separately)
    mimeType !== "application/pdf" &&
    !fileName.toLowerCase().endsWith(".pdf") &&
    // Check if it matches any of our analysis types
    (analysisExtensions.includes(extension) ||
      analysisMimeTypes.some((type) => mimeType.includes(type)))
  );
};

// Process files for message preparation
export const processFilesForMessage = (files) => {
  if (!files || !Array.isArray(files) || files.length === 0) return [];

  return files.map((file) => {
    const path = file.data?.path || file.path;
    const isImageForVision = file.isImage || file.type?.startsWith("image/");
    const isPdf =
      file.isPdf ||
      file.type === "application/pdf" ||
      file.name?.toLowerCase().endsWith(".pdf");

    // Get URL from all possible sources, prioritizing Supabase publicUrl
    const publicUrl =
      file.publicUrl ||
      file.data?.publicUrl ||
      file.visionMetadata?.publicUrl ||
      file.url ||
      file.localUrl;

    return {
      path: path,
      name: file.name,
      type: file.type,
      isImage: isImageForVision,
      isPdf: isPdf,
      file_id: file.openai_file_id || file.file_id,
      openai_file_id: file.openai_file_id || file.file_id,
      supabase_file_id: file.supabase_file_id || path,
      publicUrl: publicUrl,
      url: publicUrl,
      size: file.size,
      metadata: file.metadata || file.visionMetadata || {},
    };
  });
};

// Check Google Drive API availability
export const checkGoogleDriveApiAvailability = () => {
  try {
    // Check if Google APIs are loaded
    if (!window.google) {
      return { available: false, error: "Google APIs not loaded" };
    }

    // Check if Google Identity Services is available
    if (!window.google.accounts?.oauth2) {
      return { available: false, error: "Google Identity Services not loaded" };
    }

    // Check if we have an access token from the GoogleDriveButton component
    let accessToken = null;

    // Check if there's a stored access token in sessionStorage
    try {
      accessToken = sessionStorage.getItem("google_drive_access_token");
    } catch (e) {
      // sessionStorage might not be available
    }

    // If no token found, check if we can get it from the Google API state
    if (!accessToken && window.googleApiState?.accessToken) {
      accessToken = window.googleApiState.accessToken;
    }

    // If still no token, we need to authenticate first
    if (!accessToken) {
      return {
        available: false,
        error:
          "No access token available. Please authenticate with Google Drive first.",
      };
    }

    return { available: true, accessToken };
  } catch (error) {
    return {
      available: false,
      error: `Google Drive API check failed: ${error.message}`,
    };
  }
};
